// To-do details slide-over. Tabs: Overview / Subtasks / Activity.
// Overview holds priority + due + owner + rock+milestone link +
// labels + description. Subtasks tab is a first-class editor (each
// subtask gets its own owner + due + status). Activity is the
// comment thread.

import { useState } from "react";
import {
  Archive,
  CheckCheck,
  CheckSquare,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { tpApi } from "../api";
import { useResource } from "../hooks";
import {
  formatDateShort,
  isOverdue,
  priorityChipClass,
  statusChipClass,
} from "../utils";
import { Avatar } from "../components/Avatar";
import { ActivityFeed } from "../components/ActivityFeed";
import {
  InlineEditableText,
  InlineEditableMultiline,
  InlineEditableRichText,
} from "../components/InlineEditable";
import { KebabMenu } from "../components/KebabMenu";
import { DC } from "./createSlideOver/primitives";
import { PersonSelect, RockSelect, MilestoneSelect } from "./createSlideOver/selects";
import { PrioritySelect } from "./createSlideOver/fields";
import {
  TODO_PRIORITY,
  TODO_STATUS,
  type Subtask,
  type Todo,
} from "../../../../../../shared/team-planner/schemas";

type TodoTab = "overview" | "subtasks" | "activity";

interface TodoSheetProps {
  id: string;
  onClose: () => void;
  onChanged: () => void;
}

export function TodoSheet({ id, onClose, onChanged }: TodoSheetProps) {
  const todoRes = useResource(() => tpApi.getTodo(id), [id]);
  const todo = todoRes.data;
  const subtasks = useResource(() => tpApi.listSubtasks(id), [id]);
  const [tab, setTab] = useState<TodoTab>("overview");
  const [busy, setBusy] = useState(false);

  async function patch(input: Record<string, unknown>) {
    setBusy(true);
    try {
      await tpApi.updateTodo(id, input);
      todoRes.refresh();
      onChanged();
    } finally {
      setBusy(false);
    }
  }
  async function setStatus(s: string) {
    setBusy(true);
    try {
      await tpApi.setTodoStatus(id, s);
      todoRes.refresh();
      onChanged();
    } finally {
      setBusy(false);
    }
  }
  async function setOwner(userId: string | null) {
    if (!userId) return;
    await tpApi.setPrimaryOwner("todo", id, { subjectType: "user", subjectId: userId });
    todoRes.refresh();
    onChanged();
  }

  return (
    <>
      <div className="tp-sheet-backdrop" onClick={onClose} aria-hidden />
      <aside className="tp-sheet" role="dialog" aria-modal="true">
        <div style={{ height: 4, background: "var(--bsp-blue-2)", flex: "0 0 4px" }} />
        <header className="tp-sheet-header" style={{ flexDirection: "column", alignItems: "stretch", paddingBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "var(--bsp-blue-pale)",
                color: "var(--bsp-blue-2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <CheckSquare size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  font: "700 10px/1 var(--bsp-font-sans)",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: DC.grey,
                  marginBottom: 3,
                }}
              >
                To-do · T{todo?.seq ?? "—"}
              </div>
              {todo ? (
                <InlineEditableText
                  value={todo.title}
                  required
                  maxLength={300}
                  readStyle={{
                    font: "700 16px/1.2 var(--bsp-font-display)",
                    letterSpacing: "0.01em",
                    color: DC.dark,
                    textTransform: "uppercase",
                  }}
                  onCommit={(next) => patch({ title: next })}
                />
              ) : (
                <div style={{ font: "700 16px/1.2 var(--bsp-font-display)", color: DC.dark }}>
                  Loading…
                </div>
              )}
            </div>
            {todo ? (
              <KebabMenu
                actions={[
                  {
                    label: "Mark complete",
                    icon: <CheckCheck size={14} />,
                    disabled: todo.status === "done",
                    onClick: () => setStatus("done"),
                  },
                  { label: "_divider", divider: true, onClick: () => undefined },
                  {
                    label: "Archive to-do",
                    icon: <Archive size={14} />,
                    danger: true,
                    onClick: async () => {
                      if (!window.confirm("Archive this to-do?")) return;
                      await tpApi.archiveTodo(todo.id);
                      onChanged();
                      onClose();
                    },
                  },
                ]}
              />
            ) : null}
            <button type="button" className="tp-icon-action" onClick={onClose} aria-label="Close">
              <X size={16} />
            </button>
          </div>
          {todo ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              <span className={statusChipClass(todo.status)}>{todo.status.replace("_", " ")}</span>
              <span className={priorityChipClass(todo.priority)}>{todo.priority}</span>
              {todo.rockTitle ? (
                <span className="bsp-chip bsp-chip-blue">↪ {todo.rockTitle}</span>
              ) : null}
              <span
                style={{
                  fontSize: 12,
                  color: isOverdue(todo.dueAt, todo.status) ? DC.redFg : DC.grey,
                }}
              >
                {todo.dueAt ? `Due ${formatDateShort(todo.dueAt)}` : "No due date"}
              </span>
              {todo.isPrivate ? (
                <span className="bsp-chip bsp-chip-grey">Private</span>
              ) : null}
            </div>
          ) : null}
        </header>

        <nav
          role="tablist"
          aria-label="To-do details sections"
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0 22px",
            borderBottom: `1px solid ${DC.border}`,
            flexShrink: 0,
            background: DC.surface,
          }}
        >
          {(
            [
              { id: "overview", label: "Overview" },
              { id: "subtasks", label: "Subtasks", count: subtasks.data?.length ?? 0 },
              { id: "activity", label: "Activity" },
            ] as Array<{ id: TodoTab; label: string; count?: number }>
          ).map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.id)}
                style={{
                  padding: "11px 12px",
                  border: 0,
                  background: "transparent",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  font: "700 11px/1 var(--bsp-font-sans)",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: active ? "var(--bsp-blue)" : DC.grey,
                  borderBottom: `2px solid ${active ? "var(--bsp-blue)" : "transparent"}`,
                  marginBottom: -1,
                  cursor: "pointer",
                }}
              >
                {t.label}
                {typeof t.count === "number" && t.count > 0 ? (
                  <span
                    style={{
                      font: "700 9px/1 var(--bsp-font-mono)",
                      padding: "2px 5px",
                      background: "var(--bsp-surface-muted)",
                      borderRadius: 999,
                      color: DC.greyDeep,
                    }}
                  >
                    {t.count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </nav>

        <div style={{ flex: 1, overflowY: "auto", padding: "16px 22px 22px" }}>
          {!todo ? (
            <p style={{ color: DC.grey }}>Loading…</p>
          ) : tab === "overview" ? (
            <OverviewTab
              todo={todo}
              onPatch={patch}
              onSetStatus={setStatus}
              onSetOwner={setOwner}
              busy={busy}
            />
          ) : tab === "subtasks" ? (
            <SubtasksTab
              todoId={id}
              subtasks={subtasks.data ?? []}
              onChanged={() => {
                subtasks.refresh();
                todoRes.refresh();
                onChanged();
              }}
            />
          ) : (
            <ActivityFeed itemKind="todo" itemId={id} />
          )}
        </div>
      </aside>
    </>
  );
}

function OverviewTab({
  todo,
  onPatch,
  onSetStatus,
  onSetOwner,
  busy,
}: {
  todo: Todo;
  onPatch: (input: Record<string, unknown>) => void;
  onSetStatus: (s: string) => void;
  onSetOwner: (id: string | null) => void;
  busy: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Block title="Status & priority">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <Label>Status</Label>
            <select
              className="bsp-select"
              value={todo.status}
              disabled={busy}
              onChange={(e) => onSetStatus(e.target.value)}
            >
              {TODO_STATUS.map((s) => (
                <option key={s} value={s}>{s.replace("_", " ")}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Priority</Label>
            <PrioritySelect
              value={todo.priority as (typeof TODO_PRIORITY)[number]}
              onChange={(p) => onPatch({ priority: p ?? "low" })}
            />
          </div>
        </div>
      </Block>

      <Block title="Owner">
        {todo.ownerType === "team" && todo.primaryOwnerTeam ? (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                width: 28, height: 28, borderRadius: 4,
                background: "var(--bsp-blue-tint)", color: "var(--bsp-blue)",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                font: "700 11px/1 var(--bsp-font-sans)",
              }}
            >
              {(todo.primaryOwnerTeam.monogram ?? "··").toUpperCase()}
            </span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{todo.primaryOwnerTeam.name}</span>
          </div>
        ) : (
          <PersonSelect value={todo.primaryOwnerUserId} onChange={onSetOwner} />
        )}
      </Block>

      <Block title="Link to Rock">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <Label>Rock</Label>
            <RockSelect
              value={todo.rockId}
              onChange={(rockId) => onPatch({ rockId, milestoneId: null })}
            />
          </div>
          <div>
            <Label>Milestone</Label>
            <MilestoneSelect
              value={todo.milestoneId}
              onChange={(milestoneId) => onPatch({ milestoneId })}
              rockId={todo.rockId}
            />
          </div>
        </div>
      </Block>

      <Block title="Due date">
        <input
          type="date"
          className="bsp-input"
          value={todo.dueAt ? todo.dueAt.toISOString().slice(0, 10) : ""}
          onChange={(e) =>
            onPatch({ dueAt: e.target.value ? new Date(e.target.value).toISOString() : null })
          }
          disabled={busy}
        />
      </Block>

      {todo.labels && todo.labels.length > 0 ? (
        <Block title="Labels">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {todo.labels.map((l) => (
              <span key={l} className="bsp-chip bsp-chip-blue">{l}</span>
            ))}
          </div>
        </Block>
      ) : null}

      <Block title="Description">
        <InlineEditableRichText
          value={todo.description ?? ""}
          rows={3}
          placeholder="Click to add context, links, or acceptance criteria…"
          onCommit={(next) =>
            onPatch({ description: next.trim() === "" ? null : next })
          }
        />
      </Block>
    </div>
  );
}

function SubtasksTab({
  todoId,
  subtasks,
  onChanged,
}: {
  todoId: string;
  subtasks: Subtask[];
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [draftDue, setDraftDue] = useState("");

  async function add() {
    if (!draft.trim()) return;
    await tpApi.createSubtask(todoId, {
      title: draft.trim(),
      dueAt: draftDue ? new Date(draftDue).toISOString() : null,
    });
    setDraft("");
    setDraftDue("");
    setAdding(false);
    onChanged();
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {subtasks.length === 0 && !adding ? (
        <div style={{ color: DC.grey, fontSize: 13 }}>
          No subtasks. Break the to-do into smaller chunks if it helps.
        </div>
      ) : null}
      {subtasks.map((s) => (
        <div
          key={s.id}
          style={{
            display: "grid",
            gridTemplateColumns: "24px 1fr 120px 28px",
            gap: 8,
            alignItems: "center",
            padding: "10px 12px",
            background: DC.surface,
            border: `1px solid ${DC.border}`,
            borderLeft: "3px solid var(--ink-500)",
            borderRadius: 6,
          }}
        >
          <input
            type="checkbox"
            checked={s.status === "done"}
            onChange={async () => {
              await tpApi.updateSubtask(s.id, {
                status: s.status === "done" ? "open" : "done",
              });
              onChanged();
            }}
          />
          <input
            defaultValue={s.title}
            onBlur={async (e) => {
              if (e.target.value.trim() && e.target.value !== s.title) {
                await tpApi.updateSubtask(s.id, { title: e.target.value.trim() });
                onChanged();
              }
            }}
            style={{
              border: 0,
              background: "transparent",
              outline: 0,
              font: "400 13px/1 var(--bsp-font-sans)",
              color: s.status === "done" ? DC.grey : "var(--bsp-fg)",
              textDecoration: s.status === "done" ? "line-through" : undefined,
            }}
          />
          <input
            type="date"
            defaultValue={s.dueAt ? s.dueAt.toISOString().slice(0, 10) : ""}
            onBlur={async (e) => {
              await tpApi.updateSubtask(s.id, {
                dueAt: e.target.value ? new Date(e.target.value).toISOString() : null,
              });
              onChanged();
            }}
            style={{
              border: 0,
              background: "transparent",
              outline: 0,
              font: "400 12px/1 var(--bsp-font-mono)",
              color: DC.grey,
            }}
          />
          <button
            type="button"
            title="Delete subtask"
            onClick={async () => {
              if (!window.confirm("Delete this subtask?")) return;
              await tpApi.deleteSubtask(s.id);
              onChanged();
            }}
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 24, height: 24, border: 0, background: "transparent",
              color: DC.grey, borderRadius: 4, cursor: "pointer",
            }}
          >
            <Trash2 size={13} />
          </button>
        </div>
      ))}
      {adding ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 130px 28px", gap: 8 }}>
          <input
            className="bsp-input"
            placeholder="Subtask title"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoFocus
          />
          <input
            type="date"
            className="bsp-input"
            value={draftDue}
            onChange={(e) => setDraftDue(e.target.value)}
          />
          <button
            type="button"
            className="bsp-btn bsp-btn-primary bsp-btn-icon"
            onClick={add}
          >
            <Plus size={14} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="bsp-btn bsp-btn-secondary bsp-btn-sm"
          onClick={() => setAdding(true)}
        >
          <Plus size={13} /> Add subtask
        </button>
      )}
    </div>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        background: DC.surface,
        border: `1px solid ${DC.border}`,
        borderRadius: 8,
        padding: 14,
      }}
    >
      <div
        style={{
          font: "700 10px/1 var(--bsp-font-sans)",
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: DC.grey,
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      {children}
    </section>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        font: "700 10px/1 var(--bsp-font-sans)",
        letterSpacing: "0.10em",
        textTransform: "uppercase",
        color: DC.grey,
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  );
}
